import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import SocialMediaLinks from "@/components/SocialMediaLinks";
import { 
  Building2, 
  Users, 
  FileText, 
  Trophy, 
  Calendar, 
  CheckCircle, 
  ArrowRight,
  Clock,
  Shield,
  Award,
  Briefcase
} from "lucide-react";

const Services = () => {
  const services = [
    {
      icon: Building2,
      title: "Trade License Registration",
      description: "Complete assistance with trade license applications and renewals across all Emirates",
      price: "Starting from AED 1,000",
      duration: "3-5 business days",
      formLink: "/client-form",
      features: [
        "Document preparation and verification",
        "Government liaison and submission",
        "Fast-track processing available",
        "License renewal reminders",
        "Compliance monitoring"
      ]
    },
    {
      icon: FileText,
      title: "Business Setup",
      description: "End-to-end business establishment and VAT application support services",
      price: "Starting from AED 2,500",
      duration: "7-10 business days",
      formLink: "/client-form",
      features: [
        "Company formation consultation",
        "Legal structure advisory",
        "Registration with authorities",
        "VAT registration and application",
        "Bank account opening assistance",
        "Office space solutions"
      ]
    },
    {
      icon: Trophy,
      title: "Sports Management Consultancy",
      description: "Comprehensive sports club facilities management and consultancy services",
      price: "Starting from AED 500/month",
      duration: "Ongoing support",
      formLink: "/service-request",
      features: [
        "Sports club setup and licensing",
        "NOC support from UAE Sports Councils & Federations",
        "Facilities management and operations",
        "Sports program development",
        "Equipment procurement and maintenance",
        "Staff training and development"
      ]
    },
    {
      icon: Calendar,
      title: "Event Management",
      description: "Professional event planning and management services for all occasions",
      price: "Starting from AED 500/event",
      duration: "Project-based",
      formLink: "/service-request",
      features: [
        "Event planning and coordination",
        "Venue selection and booking",
        "Event permit issuance support",
        "Event food permit assistance",
        "Parking permit coordination",
        "Event ticketing setup",
        "Accreditation system setup",
        "Security & cleaning manning planning",
        "Logistics and vendor management",
        "Corporate and private events",
        "Marketing and promotion support"
      ]
    },
    {
      icon: Users,
      title: "HR & Compliance Services",
      description: "Comprehensive UAE-compliant HR solutions and organizational development",
      price: "Starting from AED 1,000/month",
      duration: "Ongoing support",
      formLink: "/service-request",
      features: [
        "HR Policies & Procedures (UAE-compliant)",
        "Building organization structure, Job bands, Salaries scales",
        "Building appraisal system",
        "Standardized onboarding process",
        "HR Forms & Templates (offers, contracts, NDAs)",
        "Warning letters & resignation acceptance",
        "Offboarding checklists & procedures"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-primary font-medium">Services</Link>
              <Link to="/login" className="text-foreground hover:text-primary transition-colors">Admin Login</Link>
              <div className="flex items-center space-x-4">
                <SocialMediaLinks 
                  variant="header" 
                  iconSize="h-4 w-4" 
                  className="hidden lg:flex"
                />
              </div>
              <Button asChild>
                <Link to="/apply">Get Started</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?w=1920&auto=format&fit=crop&q=80" 
            alt="Professional Business Meeting" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5"></div>
        </div>
        
        <div className="container mx-auto text-center relative z-10">
          <Badge className="mb-4" variant="secondary">Professional Services</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Comprehensive Business Solutions
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            From trade license registration to ongoing business support, we provide 
            end-to-end solutions for your business success in the UAE.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-gray-50 h-full">
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                    <div className="flex items-center justify-between pt-4">
                      <Badge variant="secondary" className="text-primary font-semibold">
                        {service.price}
                      </Badge>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 mr-1" />
                        {service.duration}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6 group-hover:bg-primary/90 transition-colors" asChild>
                      <Link to={service.formLink}>
                        Get Quote <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose ConsultPro?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We combine expertise, efficiency, and personalized service to deliver exceptional results
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Expert Team</h3>
              <p className="text-muted-foreground">
                Our certified professionals have years of experience in UAE business regulations and procedures.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Fast Processing</h3>
              <p className="text-muted-foreground">
                We ensure quick turnaround times without compromising on quality or compliance requirements.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">100% Compliance</h3>
              <p className="text-muted-foreground">
                All our services are fully compliant with UAE regulations and government requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Your Business Journey?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let us handle the complexities while you focus on growing your business. 
            Get started with a free consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary-glow">
              <Link to="/apply">
                Submit Your Application <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/crm">Access CRM</Link>
            </Button>
          </div>
          
          {/* Social Media Section */}
          <div className="mt-12">
            <p className="text-muted-foreground mb-6">Follow us for business tips and updates</p>
            <SocialMediaLinks 
              variant="contact" 
              iconSize="h-6 w-6" 
              className="justify-center"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;